/**
 * =============================================
 * UTILITÁRIOS DE DATA
 * =============================================
 * Funções auxiliares para manipulação de datas
 * =============================================
 */

/**
 * Retorna a data de hoje no formato YYYY-MM-DD (timezone local do navegador).
 * Usado como valor padrão em filtros de data para evitar carregar todo
 * o histórico de tabelas de alto volume.
 */
export const getHojeISO = (): string => {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  const dia = String(hoje.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

/**
 * Converte uma data YYYY-MM-DD (timezone local) no início do dia em ISO UTC,
 * adequado para enviar como filtro data_inicio a colunas TIMESTAMP WITH TIME ZONE.
 * Retorna undefined se a data estiver vazia/inválida.
 */
export const toInicioDiaISO = (data: string | undefined | null): string | undefined => {
  if (!data) return undefined;
  const d = new Date(`${data}T00:00:00`);
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

/**
 * Converte uma data YYYY-MM-DD (timezone local) no fim do dia em ISO UTC,
 * adequado para enviar como filtro data_fim a colunas TIMESTAMP WITH TIME ZONE.
 * Retorna undefined se a data estiver vazia/inválida.
 */
export const toFimDiaISO = (data: string | undefined | null): string | undefined => {
  if (!data) return undefined;
  const d = new Date(`${data}T23:59:59.999`);
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

/**
 * Retorna o primeiro dia do mês atual no formato YYYY-MM-DD
 */
export const getFirstDayOfMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

/**
 * Retorna o último dia do mês atual no formato YYYY-MM-DD
 */
export const getLastDayOfMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const monthStr = String(month + 1).padStart(2, '0');
  return `${year}-${monthStr}-${lastDay}`;
};

/**
 * Formata uma data para o padrão brasileiro (dd/mm/yyyy)
 */
export const formatDateBR = (date: string | Date): string => {
  if (!date) return '-';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Formata uma data e hora para o padrão brasileiro (dd/mm/yyyy HH:mm)
 */
export const formatDateTimeBR = (date: string | Date): string => {
  if (!date) return '-';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const dateHelpers = {
  getHojeISO,
  toInicioDiaISO,
  toFimDiaISO,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  formatDateBR,
  formatDateTimeBR
};

export default dateHelpers;
